import { MenuItem, type MenuItemSpec } from "prosemirror-menu";
import type { Node, NodeType } from "prosemirror-model";
import type { EditorState, NodeSelection, Selection, Transaction } from "prosemirror-state";

import { getSharedAttrs } from "../utils";

/**
 * Returns true if all top-level nodes in the selection are of the given type.
 *
 * If the selection is a NodeSelection, only the selected node is checked.
 * Otherwise, top-level nodes are checked without recursing into children.
 */
function allSelectedNodesAreType(selection: Selection, doc: Node, nodeType: NodeType): boolean {
  if ((selection as NodeSelection).node) {
    return (selection as NodeSelection).node.type === nodeType;
  }

  let result = true;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (node.type !== nodeType) result = false;
    return false; // Never recurse into child nodes
  });
  return result;
}

/**
 * Change the type of all top-level nodes in the selection to `targetType`,
 * preserving shared attrs (align, indent) from each existing node.
 */
function setBlockTypeKeepSharedAttrs(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  targetType: NodeType,
): boolean {
  const { selection, doc, tr } = state;
  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (node.type !== targetType) {
      tr.setBlockType(pos, pos + node.nodeSize, targetType, getSharedAttrs(node));
    }
    return false; // Never recurse into child nodes
  });
  dispatch?.(tr.scrollIntoView());
  return true;
}

/**
 * This is based on `blockTypeItem` from `prosemirror-menu`,
 * but it toggles between the primary node type and the secondary (e.g., paragraph).
 * `options.attrs` may be an object to provide the attributes for the changed node.
 */
export function toggleBlockTypeItem(
  primaryNodeType: NodeType,
  secondaryNodeType: NodeType,
  options: Partial<MenuItemSpec>,
) {
  const passedOptions: MenuItemSpec = {
    run: (state, dispatch) => {
      const isPrimaryType = allSelectedNodesAreType(state.selection, state.doc, primaryNodeType);
      const targetType = isPrimaryType ? secondaryNodeType : primaryNodeType;
      return setBlockTypeKeepSharedAttrs(state, dispatch, targetType);
    },
    active(state) {
      return allSelectedNodesAreType(state.selection, state.doc, primaryNodeType);
    },
  };
  Object.assign(passedOptions, options);
  return new MenuItem(passedOptions);
}
